import { useEffect, useState } from 'react'
import { httpsCallable, getFunctions } from 'firebase/functions'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEye, faEyeSlash, faCopy, faCheck } from '@fortawesome/free-solid-svg-icons'
import Loader from '../components/Loader'
import { useToast } from '../context/ToastContext'

const MaskedValue = ({ value }) => {
  const [revealed, setRevealed] = useState(false)
  const [copied, setCopied] = useState(false)

  const copyToClipboard = () => {
    navigator.clipboard.writeText(value)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  return (
    <div className='flex items-start justify-end gap-2'>
      <span className='font-mono text-sm break-all max-w-md'>
        {revealed ? value : '*****************************'}
      </span>
      <div className='flex items-center gap-1 shrink-0'>
        <button
          className='text-blue-6 hover:text-blue-8 p-1 w-6 h-6 flex items-center justify-center'
          onClick={() => setRevealed(!revealed)}
          title={revealed ? 'Hide' : 'Reveal'}
          aria-label={revealed ? 'Hide value' : 'Reveal value'}
        >
          <FontAwesomeIcon icon={revealed ? faEyeSlash : faEye} className='text-base' />
        </button>
        <button
          className={`p-1 w-6 h-6 flex items-center justify-center ${copied ? 'text-green-600' : 'text-blue-6 hover:text-blue-8'}`}
          onClick={copyToClipboard}
          title={copied ? 'Copied' : 'Copy'}
          aria-label='Copy value to clipboard'
        >
          <FontAwesomeIcon icon={copied ? faCheck : faCopy} className='text-base' />
        </button>
      </div>
    </div>
  )
}

const isValidUrl = (value) => {
  if (!value || typeof value !== 'string') return false
  try {
    const url = new URL(value.trim())
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch {
    return false
  }
}

const CustomerDashboard = ({ customer }) => {
  const { showToast } = useToast()
  const [keys, setKeys] = useState([])
  const [loading, setLoading] = useState(true)
  const [tokenData, setTokenData] = useState(null)
  const [tokenLoading, setTokenLoading] = useState(true)

  useEffect(() => {
    if (!customer || !customer.apiKeyIds || customer.apiKeyIds.length === 0) {
      setLoading(false)
      return
    }

    const fetchKeys = async () => {
      try {
        const getCustomerKeys = httpsCallable(getFunctions(), 'getCustomerKeys')
        const res = await getCustomerKeys({ apiKeyIds: customer.apiKeyIds })
        setKeys(res.data.keys || [])
      } catch (err) {
        console.error('Error fetching customer keys:', err)
        showToast('Failed to load API keys', 'red')
      }
      setLoading(false)
    }

    fetchKeys()
  }, [customer])

  // Fetch the token via cloud function (customers can't read /Tokens directly)
  useEffect(() => {
    if (!customer?.accessToken) {
      setTokenLoading(false)
      return
    }

    const fetchToken = async () => {
      try {
        const getToken = httpsCallable(getFunctions(), 'getCustomerToken')
        const res = await getToken({ tokenId: customer.accessToken })
        if (res.data) {
          setTokenData(res.data)
        }
      } catch (err) {
        console.error('Error fetching token:', err)
      }
      setTokenLoading(false)
    }

    fetchToken()
  }, [customer?.accessToken])

  if (!customer) {
    return (
      <div className='p-4'>
        <Loader />
      </div>
    )
  }

  return (
    <div className='p-4 max-w-4xl mx-auto'>
      <h1 className='text-4xl mb-6'>My Keys</h1>

      {isValidUrl(customer.supportPortalLink) && (
        <a
          href={customer.supportPortalLink}
          target='_blank'
          rel='noopener noreferrer'
          className='block w-full text-center bg-blue-600 hover:bg-blue-700 text-white text-lg font-semibold py-4 px-6 rounded-lg mb-6 shadow-sm transition-colors'
        >
          Open Support Portal
        </a>
      )}

      <div className='bg-white border rounded-lg p-5 mb-4 shadow-sm'>
        <h2 className='text-2xl mb-1'>Documentation Access Token</h2>
        <p className='text-sm text-gray-600 mb-1'>
          <a href='https://docs.aipredict.medipro.inspireht.com/' target='_blank' rel='noopener noreferrer' className='text-blue-6 hover:underline'>
            Medipro API Documentation
          </a>
        </p>
        <p className='text-xs text-gray-500 mb-3'>
          Paste access token into the Username sign in form and leave Password blank.
        </p>
        {tokenLoading && <Loader />}
        {!tokenLoading && tokenData
          ? (
            <table className='w-full text-left text-sm'>
              <thead>
                <tr className='border-b'>
                  <th className='p-2'>Name</th>
                  <th className='p-2 text-right'>Value</th>
                </tr>
              </thead>
              <tbody>
                <tr className='border-b'>
                  <td className='p-2'>Access Token</td>
                  <td className='p-2'>
                    <MaskedValue value={tokenData.token} />
                  </td>
                </tr>
              </tbody>
            </table>
            )
          : !tokenLoading && <p className='text-gray-500'>No access token is currently assigned to your account.</p>}
      </div>

      <div className='bg-white border rounded-lg p-5 mb-4 shadow-sm'>
        <h2 className='text-2xl mb-3'>API Keys</h2>
        {loading && (
          <div className='flex items-center gap-3 py-4'>
            <Loader />
            <span className='text-gray-500'>Loading API keys...</span>
          </div>
        )}
        {!loading && keys.length === 0 && (
          <p className='text-gray-500'>No API keys are currently assigned to your account.</p>
        )}
        {!loading && keys.length > 0 && (
          <table className='w-full text-left text-sm'>
            <thead>
              <tr className='border-b'>
                <th className='p-2'>Name</th>
                <th className='p-2 text-right'>Value</th>
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => (
                <tr key={k.id} className='border-b'>
                  <td className='p-2'>{k.name}</td>
                  <td className='p-2'>
                    <MaskedValue value={k.value} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

export default CustomerDashboard
